import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Avatar, CardContent, IconButton, Rating, Chip } from '@mui/material'
import { CardActionArea } from '@mui/material'
import { useSelector } from 'react-redux'
import { IChat } from '../../store/chatsOnline/types'
import { changeFio, getFormFioShort } from '../../utils/changeFio'
import { getDateFormat } from '../../utils/getDateFormat'
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { IStore } from '../../store/types'
import { ChatStatus, Roles, SIZE_PROG, STATUSES_TRANSFER, StatusWork, TypeMessage } from '../../utils/consts'
import { BadgeCount } from '../Dadge'
import { captureChat, changeStatusChat, closeChat, updateNotifications } from '../../store/chatsOnline/actions'
import { useAppDispatch } from '../../store'
import { parseLink } from '../../utils/parseLink'
import { ChatsOnlineActions } from '../../store/chatsOnline'
import NotificationsIcon from '@mui/icons-material/Notifications'
import CircularProgress from '@mui/material/CircularProgress'
import { WrapperBtnS, WrapperMesssageS } from './styles'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../../utils/routes'
import { useCountdownTimer } from '../../hooks/useCountdownTimer'
import TimerIcon from '@mui/icons-material/Timer'
import { styled } from '@mui/material/styles'

interface Props {
  chat: IChat
}

const TimerWrapperS = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.error.main,
  fontSize: '12px',
  marginTop: theme.spacing(0.5)
}))

export const ChatItemOnline: React.FC<Props> = ({ chat }) => {
  const [loadingNotif, setLoadingNotif] = React.useState(0)
  const navigate = useNavigate()

  const { chatsOnline, users } = useSelector((store: IStore) => store)
  const dispatch = useAppDispatch()

  const renderIcons = (isRead?: boolean) => {
    if (isRead) {
      return <DoneAllOutlinedIcon color={'primary'} fontSize={'small'} sx={{ fontSize: 14 }} />
    }
    return <DoneOutlinedIcon fontSize={'small'} sx={{ fontSize: 14 }} />
  }

  const handleClickArea = (chat: IChat) => {
    if (!users.user || chatsOnline.loadingChat) return null

    if (
      chat.status === ChatStatus['RAW_OFFLINE_CHAT'] &&
      chat.expert_id === 0 &&
      [Roles.CONSULTANT, Roles.TRAINEE].includes(users.user.role)
    ) {
      if (users.user.status === StatusWork.OFFLINE) return null

      dispatch(captureChat(chat.absnum)).then(() => {
        navigate(`${ROUTES.chats}/${chat.chat_id}?status=${chatsOnline.statusChat}`)
      })

      return null
    }

    navigate(`${ROUTES.chats}/${chat.chat_id}?status=${chatsOnline.statusChat}`)
    dispatch(ChatsOnlineActions.setClickChat())
  }

  const handleClickNotifications = (chat: IChat) => {
    setLoadingNotif(chat.absnum)
    dispatch(
      updateNotifications({
        chat_id: chat.absnum,
        is_notifications: chat.is_notifications ? 0 : 1,
      })
    ).then(() => setLoadingNotif(0))
  }

  const handleClickClose = (chat: IChat) => {
    if (!users.user || users.user.status === StatusWork.OFFLINE) return null
    if (chat.status === ChatStatus.RAW_OFFLINE_CHAT) {
      dispatch(closeChat(chat.absnum))
    }
    if ([ChatStatus.ACTIVE, ChatStatus.INITIATED].includes(chat.status)) {
      dispatch(changeStatusChat({ chat_id: chat.absnum, status: ChatStatus.COLLAPSED }))
    }
    navigate(ROUTES.routeChange(''))
  }

  const renderBtn = (itm: IChat) => {
    const typeColor = itm.is_notifications ? 'primary' : 'disabled'
    const isMy = users.user?.id === itm.expert_id
    const disabledNotif = loadingNotif === itm.absnum

    if (
      chatsOnline.currentChat?.absnum === itm.absnum &&
      [ChatStatus.ACTIVE, ChatStatus.RAW_OFFLINE_CHAT, ChatStatus.INITIATED].includes(itm.status)
    ) {
      return (
        <WrapperBtnS>
          {isMy ? (
            <IconButton disabled={disabledNotif} aria-label='add' onClick={() => handleClickNotifications(itm)}>
              {disabledNotif ? <CircularProgress size={SIZE_PROG} /> : <NotificationsIcon color={typeColor} />}
            </IconButton>
          ) : null}
          <IconButton aria-label='add' onClick={() => handleClickClose(itm)}>
            <CloseIcon />
          </IconButton>
        </WrapperBtnS>
      )
    }

    if (isMy) {
      return (
        <WrapperBtnS>
          <IconButton aria-label='add' onClick={() => handleClickNotifications(itm)}>
            {disabledNotif ? <CircularProgress size={SIZE_PROG} /> : <NotificationsIcon color={typeColor} />}
          </IconButton>
        </WrapperBtnS>
      )
    }
    return null
  }

  const renderInfo = (itm: IChat) => {
    if (!users.user) return ''
    if (itm.expert_id === users.user.id) return 'Чат в мене в роботі'
    if (itm.expert_fio) return itm.expert_fio
    return ''
  }

  const checkColor = (itm: IChat) => {
    if (itm.absnum === chatsOnline.currentChat?.absnum) return 'rgb(229 229 228)'
    if ([ChatStatus.PROCESSED_OFFLINE_CHAT, ChatStatus.CLOSE_CHAT].includes(itm.status)) return ''
    if (!ChatStatus.RAW_OFFLINE_CHAT && itm.expert_id === users.user?.id && itm.deadline) return '#fffcc6'
    if (!itm.expert_id) return '#fffcc6'
    return ''
  }

  const renderMesssage = () => {
    if (chat.type_id === TypeMessage.RATING)
      return <Rating name='read-only' value={chat.rating} readOnly size={'small'} />
    return (
      <Typography
        variant={'body1'}
        fontSize={14}
        style={{ pointerEvents: 'none', cursor: 'default', wordBreak: 'break-word' }}
        dangerouslySetInnerHTML={{
          __html: parseLink(chat.message),
        }}
      />
    )
  }

  const getSubscriptionColor = (subscription?: string) => {
    switch (subscription) {
      case 'PRO':
        return 'success'
      case 'STANDART':
        return 'primary'
      case 'BASIC':
        return 'default'
      default:
        return 'default'
    }
  }

  const timeLeft = useCountdownTimer({ expiresAt: chat.auto_reply_at })

  return (
    <WrapperMesssageS key={chat.absnum} style={{ background: checkColor(chat) }}>
      <Box display={'flex'} alignItems={'flex-start'} width={'100%'}>
        <CardActionArea
          disabled={chatsOnline.currentChat?.absnum === chat.absnum}
          onClick={() => handleClickArea(chat)}
        >
          <CardContent
            style={{ paddingTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Box display={'flex'} alignItems={'center'} width={'100%'}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Avatar
                  sx={
                    chat.product_color
                      ? { bgcolor: chat.product_color, color: 'white' }
                      : undefined
                  }
                >
                  {getFormFioShort(chat.client_fio)}
                </Avatar>
                {chat.subscription && (
                  <Chip
                    label={chat.subscription}
                    size="small"
                    color={getSubscriptionColor(chat.subscription)}
                    sx={{ mt: 0.5, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Box pl={2} width={'100%'} overflow={'hidden'}>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                  <Box>
                    <Typography variant={'h6'} fontSize={14} fontWeight={600}>
                      {changeFio(chat.client_fio)}
                    </Typography>
                    <Typography
                      variant={'subtitle2'}
                      color={chat.product_color || '#1A82C5FF'}
                      fontSize={13}
                    >
                      {chat.sub_name || ''}
                    </Typography>
                  </Box>

                  <Box pr={2} display={'flex'} alignItems={'center'}>
                    {renderIcons(!!chat.is_read)}
                    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                      <Typography ml={1} variant={'h6'} fontSize={12} color={'#8f8f8f'}>
                        {getDateFormat(chat.adate_message)}
                      </Typography>
                      {chat.count_not_read && chat.is_transfered !== 1 ? (
                        <BadgeCount count={chat.count_not_read} />
                      ) : null}
                    </Box>
                  </Box>
                </Box>

                {renderMesssage()}
              </Box>
            </Box>
          </CardContent>
        </CardActionArea>
        {renderBtn(chat)}
        {chatsOnline.loadingChat === chat.absnum ? (
          <CircularProgress size={SIZE_PROG} style={{ position: 'absolute', right: '50%', top: '50%' }} />
        ) : null}
      </Box>
      <Box px={2}>
        <Typography variant={'body1'} color={'#1A82C5FF'}>
          {STATUSES_TRANSFER[`${chat.is_transfered}.${chat.is_transfer}`]?.title}
        </Typography>
      </Box>
      <Box px={2} width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography variant={'body1'}>{renderInfo(chat)}</Typography>
      </Box>
      {chat.auto_reply_at && (
        <TimerWrapperS>
          <TimerIcon fontSize="small" />
          <Typography variant="caption">{timeLeft}</Typography>
        </TimerWrapperS>
      )}
    </WrapperMesssageS>
  )
}
